// Public attribution only. Email, names, free text, arbitrary query parameters,
// and provider credentials never belong in a journey URL or browser storage.
export const JOURNEY_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const JOURNEY_CAMPAIGN_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const TOKEN = /^[a-zA-Z0-9_-]{1,150}$/;
const CAMPAIGN = /^[a-zA-Z0-9][a-zA-Z0-9 ._~+:/?&#=-]{0,149}$/;
const STORAGE_KEY = "technical-leaders:growth-journey:v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function getJourneyCampaignContext(search: string): Record<string, string> {
  const incoming = new URLSearchParams(search);
  return Object.fromEntries(JOURNEY_CAMPAIGN_KEYS.flatMap((key) => {
    const value = incoming.get(key);
    return value && CAMPAIGN.test(value) ? [[key, value]] : [];
  }));
}

export function getJourneyId(search = ""): string | undefined {
  const explicit = new URLSearchParams(search).get("journey_id");
  if (explicit && JOURNEY_ID_PATTERN.test(explicit)) return explicit.toLowerCase();
  if (typeof window === "undefined") return undefined;
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null");
    if (stored && JOURNEY_ID_PATTERN.test(stored.id) && Number.isFinite(stored.at) &&
      Date.now() >= stored.at && Date.now() - stored.at < MAX_AGE_MS) return stored.id;
  } catch { /* Storage is optional. */ }
  return undefined;
}

// Call only in response to a visitor interaction, never in render/prerender.
export function getOrCreateJourneyId(search = typeof window === "undefined" ? "" : window.location.search) {
  if (typeof window === "undefined") return undefined;
  const id = getJourneyId(search) || window.crypto?.randomUUID?.();
  if (!id || !JOURNEY_ID_PATTERN.test(id)) return undefined;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id, at: Date.now() }));
  } catch { /* The explicit URL still carries the ID when storage is blocked. */ }
  return id;
}

export function getGrowthJourneyContext(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const context = getJourneyCampaignContext(search);
  for (const key of ["source", "location", "home_revision", "workflow", "library_source", "library_location"] as const) {
    const value = params.get(key);
    if (value && TOKEN.test(value)) context[key] = value;
  }
  const audience = params.get("audience");
  if (audience === "organization" || audience === "individual") context.audience = audience;
  const id = getJourneyId(search);
  if (id) context.journey_id = id;
  return context;
}

export function buildGrowthJourneyUrl(pathname: string, search: string, overrides: Record<string, string | undefined> = {}) {
  const [pathAndQuery, hash = ""] = pathname.split("#", 2);
  const [path, existing = ""] = pathAndQuery.split("?", 2);
  const destination = getGrowthJourneyContext(existing);
  if (!JOURNEY_ID_PATTERN.test(new URLSearchParams(existing).get("journey_id") || "")) delete destination.journey_id;
  const overrideSearch = new URLSearchParams(Object.entries(overrides).filter(([, value]) => value !== undefined) as [string, string][]).toString();
  const safeOverrides = getGrowthJourneyContext(overrideSearch);
  if (!JOURNEY_ID_PATTERN.test(overrides.journey_id || "")) delete safeOverrides.journey_id;
  const context = { ...getGrowthJourneyContext(search), ...destination, ...safeOverrides };
  for (const [key, value] of Object.entries(overrides)) if (value === undefined) delete context[key];
  const params = new URLSearchParams(context);
  return `${path}${params.size ? `?${params}` : ""}${hash ? `#${hash}` : ""}`;
}

const JOURNEY_PATH = /^\/(?:pt-br\/ai-call|ai-30(?:\/summary)?|ai-for-leaders|ai-for-leaders\/summary|ai-call|library|library-confirmed|fortune-100-prompt-library(?:\/[a-z0-9-]+)?|ai-workshop|join|workshop)\/?$/;

// Native capture runs before React click handlers so their events share the ID.
// Shared links deliberately start a fresh referral and must never carry it.
export function installGrowthJourneyTracking(buildCheckout?: (search: string) => string) {
  if (typeof window === "undefined" || typeof document === "undefined" || typeof document.addEventListener !== "function") return () => {};
  const handleClick = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest("a");
    if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute("download")) return;
    const raw = anchor.getAttribute("href");
    if (!raw || raw.startsWith("#")) return;
    let url: URL;
    try { url = new URL(anchor.href, window.location.origin); } catch { return; }
    if (url.searchParams.get("utm_campaign") === "prompt-library-sharing") return;
    const internal = url.origin === window.location.origin && JOURNEY_PATH.test(url.pathname);
    const checkout = buildCheckout && url.origin === "https://buy.stripe.com" && url.pathname === "/00w14n4f70g7aQ2dbCaMU0K";
    if (!internal && !checkout) return;
    const id = getOrCreateJourneyId(url.search);
    if (!id) return;
    const current = getGrowthJourneyContext(window.location.search);
    const outgoing = getGrowthJourneyContext(url.search);
    const params = new URLSearchParams({ ...current, ...outgoing, journey_id: id });
    if (/^\/(?:ai-30|ai-for-leaders)(?:\/summary)?\/?$/.test(url.pathname)) params.set("audience", "organization");
    // Location names describe this click, not the initial campaign.
    const location = url.searchParams.get("location");
    if (location && TOKEN.test(location)) params.set("location", location);
    if (checkout) {
      // A prebuilt pl1_ reference contains validated public catalog context.
      const legacy = /^pl1_(navigation|hero|starter|workflow|footer)__([a-z0-9-]+)$/.exec(url.searchParams.get("client_reference_id") || "");
      if (legacy) {
        params.set("source", "fortune-100-prompt-library");
        params.set("library_location", legacy[1]);
        params.set("workflow", legacy[2]);
      }
      anchor.href = buildCheckout(params.toString());
    } else {
      url.search = params.toString();
      anchor.href = url.toString();
    }
  };
  document.addEventListener("click", handleClick, true);
  return () => document.removeEventListener("click", handleClick, true);
}
